import { useFormik } from 'formik';
import { buildValidationSchema } from '../../utils/validationSchemas';
import { processPayment } from "../../../../../api/requests/payment-requests";
import useIdempotencyKey from "../../../../../hooks/useIdempotencyKey";
import { describeFetchError } from "../../../../../utils/apiErrors";

export default function useInstantValidation({
                                                 linkData,
                                                 selectedInstallment,
                                                 cardVisual,
                                                 formatTRY,
                                                 getTotalWithCommission,
                                                 setErrorMessage,
                                                 isSubmitting,
                                                 setIsSubmitting,
                                                 linkId,
                                                 showCustomerForm,
                                                 showOnlyEmail,
                                                 requiresCustomerInfo,
                                                 contractText
                                             }) {
    /*
     * Ödeme linkini açan kişi çift tıklarsa ya da yavaş bağlantıda tekrar
     * gönderirse iki ayrı kart çekimi olurdu. Anahtar form açıldığında bir kez
     * üretilir; 3DS yanıtı belgeyi değiştirdiği için başarıda sıfırlanmaz.
     */
    const paymentIdempotency = useIdempotencyKey();

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            amount: linkData?.fixedAmount ? Number(linkData.fixedAmount).toFixed(2) : '',
            cc_owner: '',
            card_number: '',
            expiry_month: '',
            expiry_year: '',
            cvv: '',
            installment_count: '1',
            customer_name: '',
            customer_email: '',
            customer_phone: '',
            customer_address: '',
            customer_district: '',
            customer_province: '',
            customer_country: 'Türkiye',
            accountType: '',
            tcNumber: '',
            taxNumber: '',
            taxOffice: '',
            companyName: ''
        },
        validationSchema: buildValidationSchema({ showCustomerForm, showOnlyEmail, requiresCustomerInfo }),
        // useInstantValidation.js - onSubmit içinde

        onSubmit: async (values) => {
            if (!linkData) {
                setErrorMessage('Link verisi bulunamadı');
                setIsSubmitting(false);
                return;
            }

            const cardBrand = cardVisual?.brand || '';

            if (!cardBrand) {
                console.warn('Card brand not detected');
                setErrorMessage('Kart markası tespit edilemedi. Lütfen kart numarasını kontrol ediniz.');
                setIsSubmitting(false);
                return;
            }

            const baseAmount = parseFloat(values.amount);

            // Sabit (FIXED) linklerde sunucu da doğrular; burada erken uyarı verilir
            if (linkData.minAmount != null && baseAmount < Number(linkData.minAmount)) {
                setErrorMessage(`Tutar en az ₺${Number(linkData.minAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} olmalıdır`);
                setIsSubmitting(false);
                return;
            }
            if (linkData.maxAmount != null && baseAmount > Number(linkData.maxAmount)) {
                setErrorMessage(`Tutar en fazla ₺${Number(linkData.maxAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} olabilir`);
                setIsSubmitting(false);
                return;
            }

            const totalAmount = getTotalWithCommission(baseAmount, selectedInstallment.commission);
            setIsSubmitting(true);

            const isFullCustomerForm = showCustomerForm && requiresCustomerInfo;

            const userName = showCustomerForm ? (showOnlyEmail ? linkData.invoiceCompanyName : values.customer_name) : linkData.invoiceCompanyName;
            const userPhone = showCustomerForm ?
                (showOnlyEmail ? linkData.invoicePhone : values.customer_phone)
                : linkData.invoicePhone;
            const userAddress = isFullCustomerForm ? `${values.customer_address}, ${values.customer_district}/${values.customer_province}` : (linkData.invoiceAddress || undefined);

            const invoiceCompanyName = isFullCustomerForm ? values.companyName || values.customer_name : (linkData.invoiceCompanyName || undefined);
            const invoiceAccountType = isFullCustomerForm ? values.accountType : (linkData.invoiceAccountType || undefined);
            const invoiceTaxNumber = isFullCustomerForm && values.accountType !== 'SOLE_PROPRIETORSHIP' ? values.taxNumber : (linkData.invoiceTaxNumber || undefined);
            const invoiceTcNumber = isFullCustomerForm && values.accountType === 'SOLE_PROPRIETORSHIP' ? values.tcNumber : (linkData.invoiceTcNumber || undefined);
            const invoiceTaxOffice = isFullCustomerForm ? values.taxOffice : (linkData.invoiceTaxOffice || undefined);

            try {
                const body = {
                    companyId: linkData.accountGroup.companyId,
                    accountGroupId: linkData.accountGroup.id,
                    baseAmount: +baseAmount.toFixed(2),
                    calculatedAmount: +totalAmount.toFixed(2),
                    ccOwner: values.cc_owner,
                    cardType: cardBrand,
                    userName,
                    email: showCustomerForm ? values.customer_email : linkData.invoiceEmail,
                    installmentRate: selectedInstallment.count,
                    comissionRate: +Number(selectedInstallment.commission || 0).toFixed(2),
                    cardNumber: values.card_number.replace(/\s/g, ''),
                    expiryMonth: values.expiry_month,
                    expiryYear: values.expiry_year,
                    cvv: values.cvv,
                    userPhone,
                    userAddress,
                    invoiceAddress: userAddress,
                    paymentLinkId: linkId,
                    accountId: linkData.accountId || undefined,
                    invoiceCompanyName,
                    invoiceAccountType,
                    invoiceTaxNumber,
                    invoiceTcNumber,
                    invoiceTaxOffice,
                    contractText: contractText || ''
                };

                // 🔍 DEBUG: Backend'e göndermeden önce kontrol et
                console.log('=== PAYMENT REQUEST DEBUG ===');
                console.log('Body:', JSON.stringify(body, null, 2));

                // Zorunlu alanları kontrol et
                const requiredFields = ['companyId', 'accountGroupId', 'baseAmount', 'calculatedAmount', 'ccOwner', 'cardType', 'paymentLinkId', 'installmentRate', 'comissionRate', 'cardNumber', 'expiryMonth', 'expiryYear', 'cvv'];
                const missingFields = requiredFields.filter(field => !body[field] && body[field] !== 0);
                if (missingFields.length > 0) {
                    console.error('❌ Missing required fields:', missingFields);
                    setErrorMessage(`Eksik alanlar: ${missingFields.join(', ')}`);
                    setIsSubmitting(false);
                    return;
                }

                // Regex validasyonları
                if (!/^\d{13,19}$/.test(body.cardNumber.replace(/\s/g, ''))) {
                    console.error('❌ Invalid cardNumber format');
                    setErrorMessage('Kart numarası formatı geçersiz');
                    setIsSubmitting(false);
                    return;
                }
                if (!/^\d{2}$/.test(body.expiryMonth)) {
                    console.error('❌ Invalid expiryMonth format');
                    setErrorMessage('Ay formatı geçersiz');
                    setIsSubmitting(false);
                    return;
                }
                if (!/^\d{2}$/.test(body.expiryYear)) {
                    console.error('❌ Invalid expiryYear format');
                    setErrorMessage('Yıl formatı geçersiz');
                    setIsSubmitting(false);
                    return;
                }
                if (!/^\d{3,4}$/.test(body.cvv)) {
                    console.error('❌ Invalid CVV format');
                    setErrorMessage('CVV formatı geçersiz');
                    setIsSubmitting(false);
                    return;
                }

                console.log('✅ All validations passed');

                const resp = await processPayment(body, paymentIdempotency.keyRef.current);

                const cType = resp.headers.get('content-type') || '';
                let htmlToRender = null;

                if (cType.includes('application/json')) {
                    const json = await resp.json();
                    console.log('Payment response:', json);

                    if (resp.ok && json.statusCode === 200) {
                        htmlToRender = json.data;
                        setErrorMessage('');
                    } else {
                        // Sunucu reddetti → kart işlemi başlamadı; kullanıcı formu
                        // düzeltip tekrar gönderebilsin diye anahtar tazelenir.
                        // 409/503'te tazelenmez (bkz. utils/apiErrors.js).
                        const detail = describeFetchError(resp, json, 'Ödeme işlemi başarısız oldu');
                        if (detail.shouldResetKey) paymentIdempotency.reset();
                        setErrorMessage(detail.message);
                    }
                } else {
                    htmlToRender = await resp.text();
                    setErrorMessage('');
                }

                if (htmlToRender) {
                    document.open('text/html', 'replace');
                    document.write(htmlToRender);
                    document.close();
                }
            } catch (error) {
                console.error('Payment error:', error);
                setErrorMessage('Ödeme sırasında teknik bir hata oluştu. Lütfen tekrar deneyiniz.');
            } finally {
                setIsSubmitting(false);
            }
        }
    });

    return validation;
}